import { useEffect, useState } from 'react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { AlertCircle, ExternalLink, Github, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import ApiService from '@/services/api.service';
import {
  useAvailableRepos,
  useDisconnectSourceRepo,
  useSetSourceRepo,
  useSourceRepo,
} from '@/hooks/useGit';
import type { GitRepoAuthMode, GitSyncWorkflow } from '@/services/git.service';
import { toastService } from '@/services/toast.service';

/**
 * Which repo this tenant's project lives in, and how changes reach it.
 *
 * Two shapes, chosen by `auth_mode`:
 *   PLIABLE_APP   a repo we own in pliable-inc — provisioned at `plb login`,
 *                 nothing to configure here.
 *   CUSTOMER_APP  the customer's own repo. They install the Pliable GitHub App on
 *                 their org and pick which repos it can see; we then read that
 *                 list rather than asking anyone to type a URL.
 *
 * The repo and project directory lock once anything has been imported: changing
 * them would delete every model the new project does not contain, so it is a
 * deliberate disconnect rather than a field edit.
 */
/**
 * Installing the App means leaving the page entirely — out to GitHub, back through
 * our OAuth callback, and finally here again on a fresh page load. Choosing "our
 * own repository" is local React state, so without carrying it across the user
 * returns to a card that has reset to "Pliable-managed" and has to say it twice.
 *
 * The choice can't simply be saved on the way out: it isn't a valid configuration
 * yet (no repository has been picked), and persisting a half-written one would
 * leave the tenant in a state the rest of the system has to tolerate. So the
 * *intent* rides in localStorage for the round trip and is cleared once a real
 * configuration is saved. A TTL keeps a forgotten one from pre-selecting the wrong
 * thing days later.
 */
const AUTH_MODE_INTENT_KEY = '_plb_repo_auth_mode_intent';
const AUTH_MODE_INTENT_TTL_MS = 60 * 60 * 1000;

function rememberAuthModeIntent(mode: GitRepoAuthMode) {
  try {
    localStorage.setItem(AUTH_MODE_INTENT_KEY, JSON.stringify({ mode, at: Date.now() }));
  } catch {
    // Private windows and blocked site data — the user just re-picks on return.
  }
}

function takeAuthModeIntent(): GitRepoAuthMode | null {
  try {
    const raw = localStorage.getItem(AUTH_MODE_INTENT_KEY);
    if (!raw) return null;
    const { mode, at } = JSON.parse(raw) as { mode?: GitRepoAuthMode; at?: number };
    if (!mode || !at || Date.now() - at > AUTH_MODE_INTENT_TTL_MS) {
      localStorage.removeItem(AUTH_MODE_INTENT_KEY);
      return null;
    }
    return mode;
  } catch {
    return null;
  }
}

function clearAuthModeIntent() {
  try {
    localStorage.removeItem(AUTH_MODE_INTENT_KEY);
  } catch {
    /* nothing to clean up */
  }
}

export default function SourceRepoCard() {
  const { data: config, isLoading, refetch } = useSourceRepo();
  const setSourceRepo = useSetSourceRepo();
  const disconnect = useDisconnectSourceRepo();

  const [authMode, setAuthMode] = useState<GitRepoAuthMode>('PLIABLE_APP');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('develop');
  const [prodBranch, setProdBranch] = useState('');
  const [branchTouched, setBranchTouched] = useState(false);
  const [subdir, setSubdir] = useState('');
  const [workflow, setWorkflow] = useState<GitSyncWorkflow>('DIRECT');
  const [writesDisabled, setWritesDisabled] = useState(false);
  const [depsEnabled, setDepsEnabled] = useState(false);

  const external = authMode === 'CUSTOMER_APP';
  const locked = Boolean(config?.synced);
  // Binding the tenant to a repository decides which project the import deletes
  // models against, so the API accepts it from an Admin only. Shown read-only to
  // everyone else rather than letting them fill the form in and collect a 403.
  const { isAdmin } = useUserRoles();
  // Told by the platform rather than inferred from whether listing repos failed —
  // "not installed" and "GitHub is down" need different advice.
  const appInstalled = Boolean(config?.github_installed);
  const { data: repos, isLoading: reposLoading, error: reposError, refetch: refetchRepos } =
    useAvailableRepos(external && appInstalled);
  const needsInstall = external && !appInstalled;

  useEffect(() => {
    if (!config?.configured) {
      // Nothing saved yet. If they set off to install the App, land them back on
      // the choice they already made rather than on the default.
      const intent = takeAuthModeIntent();
      if (intent) setAuthMode(intent);
      return;
    }
    setAuthMode(config.auth_mode ?? 'PLIABLE_APP');
    setRepo(config.repo ?? '');
    setBranch(config.branch ?? 'develop');
    setProdBranch(config.prod_branch ?? '');
    setBranchTouched(true);
    setSubdir(config.project_subdir ?? '');
    setWorkflow(config.sync_workflow ?? 'DIRECT');
    setWritesDisabled(Boolean(config.writes_disabled));
    setDepsEnabled(Boolean(config.dbt_deps_enabled));
  }, [config]);

  /**
   * An authenticated call rather than a link: the install URL has to carry a state
   * minted for this session. The App requests OAuth during installation, so GitHub
   * returns a first install straight to `/github/verify` with a code, and that code
   * is refused unless the state it echoes back is ours.
   */
  const [startingInstall, setStartingInstall] = useState(false);
  const startInstall = async () => {
    rememberAuthModeIntent(authMode);
    setStartingInstall(true);
    try {
      const res = (await ApiService.getInstance().request('POST', '/github/install-url')) as {
        url: string;
      };
      window.location.href = res.url;
    } catch (err) {
      setStartingInstall(false);
      toastService.error(err instanceof Error ? err.message : 'Could not start the GitHub install.');
    }
  };

  /**
   * Picking a repository pre-fills the import branch from that repository's own
   * default. Our field defaults to `develop`, which is right for Pliable-owned
   * repos and wrong for most others — and the mismatch only surfaces later, as a
   * clone failing on a branch that doesn't exist.
   *
   * Only pre-fills while the field is still untouched, so it can't overwrite a
   * branch someone deliberately chose.
   */
  const onRepoSelected = (fullName: string) => {
    setRepo(fullName);
    if (branchTouched) return;
    const picked = (repos ?? []).find((r) => r.full_name === fullName);
    if (picked?.default_branch) setBranch(picked.default_branch);
    // The production branch is deliberately NOT guessed. Getting it wrong points
    // publishing at the wrong branch, and leaving it empty is a safe, working
    // configuration — publishing simply stays inside Pliable.
  };

  const onSave = async () => {
    if (!repo) {
      toastService.error('Pick a repository first.');
      return;
    }
    try {
      await setSourceRepo.mutateAsync({
        full_name: repo,
        auth_mode: authMode,
        branch,
        prod_branch: prodBranch || null,
        project_subdir: subdir || null,
        sync_workflow: workflow,
        writes_disabled: writesDisabled,
        dbt_deps_enabled: depsEnabled,
      });
      clearAuthModeIntent();
      toastService.success('Repository settings saved.');
      refetch();
    } catch (err) {
      toastService.error(err instanceof Error ? err.message : 'Could not save repository settings.');
    }
  };

  const onDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      toastService.success('Repository disconnected.');
      refetch();
    } catch (err) {
      toastService.error(err instanceof Error ? err.message : 'Could not disconnect.');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading repository settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Repository</CardTitle>
        <CardDescription>
          Where this tenant's dbt project lives, and how changes get into Pliable.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="auth-mode">Repository owner</Label>
          <Select
            value={authMode}
            onValueChange={(v) => setAuthMode(v as GitRepoAuthMode)}
            disabled={locked || !isAdmin}
          >
            <SelectTrigger id="auth-mode" className="max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PLIABLE_APP">Pliable-managed repository</SelectItem>
              <SelectItem value="CUSTOMER_APP">Our own GitHub repository</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {external
              ? 'Pliable reads and writes a repository you own, through the Pliable GitHub App.'
              : 'Pliable provisions and owns the repository. Nothing else to configure.'}
          </p>
        </div>

        {external && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="repo">Repository</Label>
                <Button variant="ghost" size="sm" onClick={() => refetchRepos()} disabled={reposLoading}>
                  <RefreshCw className={`mr-1 h-3 w-3 ${reposLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {needsInstall ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>
                      The Pliable GitHub App isn't connected to this tenant yet. Install it on the
                      organization that owns the repository and grant it access to that repository.
                      You'll be brought back here afterwards.
                    </p>
                    <Button size="sm" variant="secondary" onClick={startInstall} disabled={startingInstall}>
                      <Github className="mr-1 h-3 w-3" /> Install the GitHub App
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : reposError ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>
                      The App is installed, but Pliable couldn't list its repositories:{' '}
                      {reposError instanceof Error ? reposError.message : 'GitHub returned an error.'}
                    </p>
                    <Button size="sm" variant="secondary" onClick={() => refetchRepos()}>
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : repos && repos.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>
                      The App is installed but has access to no repositories. Grant it access to the
                      one you want, then refresh.
                    </p>
                    <Button size="sm" variant="secondary" onClick={startInstall} disabled={startingInstall}>
                      <Github className="mr-1 h-3 w-3" /> Manage repository access
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={repo} onValueChange={onRepoSelected} disabled={locked || !isAdmin || reposLoading}>
                  <SelectTrigger id="repo" className="max-w-md">
                    <SelectValue placeholder={reposLoading ? 'Loading…' : 'Select a repository'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(repos ?? []).map((r) => (
                      <SelectItem key={r.full_name} value={r.full_name}>
                        {r.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {locked && (
                <p className="text-xs text-muted-foreground">
                  Locked once models have been imported — moving a tenant to a different repository
                  would delete every model the new one doesn&apos;t contain. Disconnect to change it.
                  Everything below can still be edited.
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="branch">Branch Pliable imports from</Label>
                <Input
                  id="branch"
                  value={branch}
                  onChange={(e) => {
                    setBranchTouched(true);
                    setBranch(e.target.value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  The branch Pliable reads from and writes to. Pre-filled from the repository's
                  default.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prod-branch">Production branch (optional)</Label>
                <Input
                  id="prod-branch"
                  value={prodBranch}
                  onChange={(e) => setProdBranch(e.target.value)}
                  placeholder="not published through git"
                />
                <p className="text-xs text-muted-foreground">
                  {prodBranch
                    ? `Publishing merges ${branch || 'the import branch'} into ${prodBranch} and imports the result.`
                    : 'Leave empty to publish inside Pliable, as it does today.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subdir">dbt project directory</Label>
                <Input
                  id="subdir"
                  value={subdir}
                  onChange={(e) => setSubdir(e.target.value)}
                  placeholder="repository root"
                  disabled={locked || !isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  {locked
                    ? 'Locked once models have been imported. Disconnect to point this tenant at a different project.'
                    : 'Where dbt_project.yml lives, if not at the root.'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workflow">How changes reach Pliable</Label>
              <Select value={workflow} onValueChange={(v) => setWorkflow(v as GitSyncWorkflow)}>
                <SelectTrigger id="workflow" className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIRECT">Push straight to the branch</SelectItem>
                  <SelectItem value="PULL_REQUEST">Open a pull request for review</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {workflow === 'PULL_REQUEST'
                  ? 'plb sync pushes a working branch and opens a PR. Nothing is imported until it is merged.'
                  : 'plb sync pushes onto the import branch and Pliable imports it immediately.'}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="writes">Read-only (never write to this repository)</Label>
                  <p className="text-xs text-muted-foreground">
                    Blocks Push to Develop. Changes reach Pliable through git only.
                  </p>
                </div>
                <Switch id="writes" checked={writesDisabled} onCheckedChange={setWritesDisabled} />
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="deps">Run dbt deps before each build</Label>
                  <p className="text-xs text-muted-foreground">
                    Needed if the project has a <span className="font-mono">packages.yml</span>.
                    Adds a few seconds per build.
                  </p>
                </div>
                <Switch id="deps" checked={depsEnabled} onCheckedChange={setDepsEnabled} />
              </div>
            </div>
          </>
        )}

        {isAdmin ? (
          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={setSourceRepo.isPending}>
              {setSourceRepo.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
            {config?.configured && (
              <Button variant="outline" onClick={onDisconnect} disabled={disconnect.isPending}>
                Disconnect
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Only an Admin can change which repository this tenant uses.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
